
import { ItemCompact } from "@/models/ItemCompact";
import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import { FilterRecipesNames } from "@/models/Recipes/FilterRecipesNames";
import { RecipeCategory } from "@/models/Recipes/RecipeCategory";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import { getAllergenStatusMeta } from "@/utils/allergenStatus";
import RecipesRepository from "@/utils/API/RecipesRepository";
import { getBoldString } from "@/utils/Helpers";
import {
  buildRecipeListingFilters,
  collectRecipesFromCategoryResults,
  filterRecipeSearchResultsByCategory,
} from "@/utils/recipeListingSearch";
import { Vue, Component, Prop } from "vue-property-decorator";
import { Snotify } from "vue-snotify";
import { mapState } from "vuex";
import VAllergenStatusIcon from "@/components/allergens/VAllergenStatusIcon.vue";
import vSelectFx from "@/components/vSelectFx.vue";
import vPreLoader from "./vPreLoader.vue";
import {Recipe} from "@/models/Recipes/Recipe";

interface RecipesState {
  // Grid properties
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  orders: Ordering;
  filters: Filter;
  loading: boolean;

  // List of displayed recipes
  recipes: ItemCompact[];
  // Selected recipe
  selectedRecipeId: number;
  // Recipe search data (recipes by category)
  categories: FilterRecipesNames[];
}

interface RecipeListingSearchState {
  selectedCategoryId: number | null;
  searchTerm: string;
}

@Component({
  components: {
    VAllergenStatusIcon,
    vPreLoader,
    vSelectFx,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})

export default class vRecipeListing extends Vue {
  @Prop() isRecipe : boolean;

  private readonly recipeListingSearchStateKeyPrefix = "recipeListingSearchState";

  localizationState: any;
  accountState: any;
  checked: number[] = [];
  selectedCategoryId: number | null = null;
  availableCategories: RecipeCategory[] = [];
  searchTerm = "";

  recipesState: RecipesState = {
    count: 0,
    totalCount: 0,
    page: 1,
    pageSize: 10,
    orders: { field: "Name", orderType: OrderType.Asc },
    filters: { name: "Name", value: "" },
    loading: true,

    recipes: [],
    selectedRecipeId: 0,
    categories: [],
  };

  get translations() {
    return this.localizationState.translations;
  }

  get isAllergenAiAddonActive() {
    if (this.accountState?.userDetails?.isTrial || this.$store.getters.getUserDetails?.isTrial) {
      return true;
    }

    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get recipeListColumnCount() {
    return this.isAllergenAiAddonActive ? 5 : 4;
  }

  get recipeListingSearchStateKey() {
    return `${this.recipeListingSearchStateKeyPrefix}:${this.isRecipe ? "recipe" : "combined"}`;
  }

  getAllergenStatusVariant(recipe: ItemCompact) {
    return getAllergenStatusMeta(recipe).variant;
  }

  getAllergenStatusTitle(recipe: ItemCompact) {
    return getAllergenStatusMeta(recipe).title;
  }

  /**
   * Helper method for enabling / disabling remove button
   *
   * @return {boolean}
   */
  get isRemoveDisabled() {
    return this.checked.length === 0;
  }

  /**
   * Centralized method for fetching recipes and storing to state
   */
  async requestRecipes(listRequest: ListRequest) {
    //this.recipesState.loading = true;
    try {
      const response = await RecipesRepository.listReducedRecipes(listRequest, this.isRecipe);

      this.recipesState.recipes = response.data.result;
      // this.recipesState.recipes.map((recipe: any) => {
      //   recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
      // });
      this.recipesState.count = response.data.count;
      this.recipesState.totalCount = this.isRecipe
        ? response.data.totalCount
        : await this.resolveCombinedRecipeTotalCount(listRequest, response.data.result, response.data.totalCount);
      this.recipesState.loading = false;
    }
    catch (error: any) {
      if (error.response) {
        this.$snotify.error(
          error.response.data.errorMessages[0].userVisibleMessage
        );
      }
      else this.$snotify.error(this.translations.get("network-error"));
    }
  }

  async resolveCombinedRecipeTotalCount(
    listRequest: ListRequest,
    currentPageResults: ItemCompact[],
    reportedTotalCount: number,
  ) {
    const currentPageCount = Array.isArray(currentPageResults) ? currentPageResults.length : 0;

    if (reportedTotalCount === currentPageCount && this.recipesState.page === 1) {
      return reportedTotalCount;
    }

    try {
      const fullCountRequest = new ListRequest({
        page: 1,
        pageSize: 5000,
        filters: listRequest.queryOptions.filters,
        orders: listRequest.queryOptions.orders,
      });
      const countResponse = await RecipesRepository.listReducedRecipes(fullCountRequest, false);
      return Array.isArray(countResponse.data?.result)
        ? countResponse.data.result.length
        : currentPageCount;
    }
    catch {
      return currentPageCount;
    }
  }

  buildCurrentRecipeListRequest() {
    return new ListRequest({
      page: this.recipesState.page,
      pageSize: this.recipesState.pageSize,
      filters: buildRecipeListingFilters(this.selectedCategoryId, this.recipesState.filters),
      orders: [this.recipesState.orders],
    });
  }

  /**
   * Method which handles ordering of the list of recipes (A-Z / Z-A)
   */
  orderPageSize(value: any) {
    this.recipesState.pageSize = value;

    if (value === "0") {
      this.recipesState.pageSize = 10;
    }
    else if (value === "1") {
      this.recipesState.pageSize = 20;
    }
    else if (value === "2") {
      this.recipesState.pageSize = 50;
    }
    else if (value === "3") {
      this.recipesState.pageSize = 100;
    }

    this.requestRecipes(
      this.buildCurrentRecipeListRequest()
    );
  }

  /**
   * Method which handles ordering of the list of recipes (A-Z / Z-A)
   */
  order(value: OrderType) {
    this.recipesState.orders.orderType = value;

    this.requestRecipes(
      this.buildCurrentRecipeListRequest()
    );
  }

  /**
   * Method for handling user click on Next on the recipes listing.
   * Requests the next page and stores data into state
   */
  next() {
    if (!this.disabledNext) {
      this.recipesState.page = this.recipesState.page + 1;

      this.requestRecipes(
        this.buildCurrentRecipeListRequest()
      );
    }
  }

  /**
   * Method for handling user click on Prev on the recipes listing.
   * Requests the previous page and stores data into state
   */
  prev() {
    if (this.recipesState.page > 1) {
      this.recipesState.page = this.recipesState.page - 1;

      this.requestRecipes(
        this.buildCurrentRecipeListRequest()
      );
    }
  }

  /**
   * Disabling next button if there isn't next page
   *
   * @return {boolean}
   */
  get disabledNext() {
    const totalPages = Math.ceil(this.recipesState.totalCount / this.recipesState.pageSize);

    return totalPages <= 1 || this.recipesState.page >= totalPages;
  }

  get paginationSummary() {
    if (this.recipesState.loading) {
      return "";
    }

    if (this.recipesState.totalCount === 0 || this.recipesState.recipes.length === 0) {
      return `0 ${this.paginationItemLabelPlural}`;
    }

    const rangeStart = ((this.recipesState.page - 1) * this.recipesState.pageSize) + 1;
    const rangeEnd = rangeStart + this.recipesState.recipes.length - 1;
    const itemLabel = this.recipesState.totalCount === 1
      ? this.paginationItemLabelSingular
      : this.paginationItemLabelPlural;

    return `${rangeStart}-${rangeEnd} of ${this.recipesState.totalCount} ${itemLabel}`;
  }

  get paginationItemLabelSingular() {
    return this.isRecipe ? "recipe" : "combined recipe";
  }

  get paginationItemLabelPlural() {
    return this.isRecipe ? "recipes" : "combined recipes";
  }

  get selectedCategoryName() {
    if (!this.selectedCategoryId) {
      return "";
    }

    const selectedCategory = this.availableCategories.find(
      (category: RecipeCategory) => category.id === this.selectedCategoryId
    );

    return selectedCategory?.name || "";
  }


  /**
   * Method which returns false if there is only one page of the recipe list
   * If true, Prev and Next buttons are shown
   *
   * @return {boolean}
   */
  get showControls() {
    return this.recipesState.totalCount > this.recipesState.count;
  }

  onAuditLogClick(recipe: ItemCompact) {
    this.$emit("onAuditLogClick", recipe);
  }

  onDeleteRecipeClick(){
    this.$emit("onDeleteRecipeClick", this.checked, this.removeRecipes);
  }

  /**
   * Hides all modals and resets some state variables
   */
  resetState() {
    this.recipesState.selectedRecipeId = 0;
    this.checked.length = 0;
    this.checked = [];
  }

  chooseRecipe(recipeId: number) {
    this.$emit("chooseRecipe", recipeId);
  }

  /**
   * Method which requests delete recipes to API and shows toastr message
   */
  removeRecipes(checked: number[]) {
    this.$snotify.async(
      this.translations.get("rec_toastr_deleting"),
      () => <Promise<Snotify>>new Promise((resolve, reject) => {
        RecipesRepository.deleteRecipes({ recipeIds: checked })
          .then(() => {
            this.recipesState.page = 1;
            this.requestRecipes(
              this.buildCurrentRecipeListRequest()
            );
            this.resetState();
            $("#searchRecipes").val("").trigger("change");
            resolve({
              body: this.isRecipe ? this.translations.get("rec_toastr_deleted") : this.translations.get("rec_copy_combined_toastr_deleted"),
            });
          })
          .catch((error) => {
            let message = "";
            if (error.response) {
              message =
                error.response.data.errorMessages[0].userVisibleMessage;
            }
            else {
              message = this.translations.get("network_error");
            }
            reject({ body: message });
          });
      }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "recipes", "dishes", "ingredients"],
      })
      .then(() => {
        document.title =
          this.translations.get("rec_title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.loadRecipeListingSearchState();
    this.requestRecipes(
      this.buildCurrentRecipeListRequest()
    );
  }


  async mounted() {
    (<any>window).$.Pages.init();
    await this.loadRecipeCategories();
    this.emitCategorySelectionChanged();
    this.initCategorySearch();
    this.initSearch();
    this.restoreSearchControls();
  }

  loadRecipeListingSearchState() {
    try {
      const storedState = window.sessionStorage.getItem(this.recipeListingSearchStateKey);
      if (!storedState) {
        return;
      }

      const parsedState = JSON.parse(storedState) as Partial<RecipeListingSearchState> & {
        activeFilter?: Filter;
        selectedRecipeSearchOption?: { text?: string };
      };
      const storedSearchTerm =
        parsedState.searchTerm ||
        parsedState.selectedRecipeSearchOption?.text ||
        (parsedState.activeFilter?.name === "Name" ? String(parsedState.activeFilter.value || "") : "");

      this.selectedCategoryId = parsedState.selectedCategoryId || null;
      this.searchTerm = storedSearchTerm;
      this.recipesState.filters = storedSearchTerm
        ? { name: "Name", value: storedSearchTerm }
        : { name: "", value: "" };
      this.emitCategorySelectionChanged();
    }
    catch {
      this.clearRecipeListingSearchState();
    }
  }

  saveRecipeListingSearchState() {
    try {
      const state: RecipeListingSearchState = {
        selectedCategoryId: this.selectedCategoryId,
        searchTerm: this.searchTerm,
      };

      window.sessionStorage.setItem(this.recipeListingSearchStateKey, JSON.stringify(state));
    }
    catch {
      // Ignore storage failures; search still works for the current component instance.
    }
  }

  clearRecipeListingSearchState() {
    try {
      window.sessionStorage.removeItem(this.recipeListingSearchStateKey);
    }
    catch {
      // Ignore storage failures.
    }
  }

  emitCategorySelectionChanged() {
    this.$emit("category-selection-changed", {
      id: this.selectedCategoryId,
      name: this.selectedCategoryName,
    });
  }

  restoreSearchControls() {
    if (this.isRecipe) {
      $("#searchCategories")
        .val(this.selectedCategoryId ? String(this.selectedCategoryId) : "all")
        .trigger("change.select2");
    }

    if (this.searchTerm) {
      const selectedSearchTermOption = new Option(
        this.searchTerm,
        this.searchTerm,
        true,
        true,
      );

      $("#searchRecipes")
        .append(selectedSearchTermOption)
        .trigger("change.select2");
    }
  }

  async loadRecipeCategories() {
    if (!this.isRecipe) {
      return;
    }

    try {
      const response = await RecipesRepository.listRecipeCategories();
      const categories = Array.isArray(response.data?.result)
        ? response.data.result
        : Array.isArray(response.data)
          ? response.data
          : [];
      this.availableCategories = categories
        .slice()
        .sort((a: RecipeCategory, b: RecipeCategory) => a.name.localeCompare(b.name));
    }
    catch (error: any) {
      this.availableCategories = [];

      if (error?.response) {
        this.$snotify.error(
          error.response.data.errorMessages[0].userVisibleMessage
        );
      }
      else {
        this.$snotify.error(this.translations.get("network-error"));
      }
    }
  }

  initCategorySearch() {
    if (!this.isRecipe) {
      return;
    }

    const $categorySelect = $("#searchCategories");

    if ((<any>$categorySelect).hasClass("select2-hidden-accessible")) {
      (<any>$categorySelect).select2("destroy");
    }

    $categorySelect.empty();
    $categorySelect.append(new Option("All", "all", true, true));

    this.availableCategories.forEach((category: RecipeCategory) => {
      if (category.name) {
        $categorySelect.append(new Option(category.name, String(category.id), false, false));
      }
    });

    (<any>$categorySelect)
      .select2({
        allowClear: false,
        placeholder: "All",
        minimumResultsForSearch: 0,
        width: "100%",
      })
      .on("select2:select", (eventData: any) => {
        const value = String(eventData?.params?.data?.id || "");
        this.selectedCategoryId = value && value !== "all" ? Number(value) : null;
        this.recipesState.page = 1;
        this.recipesState.orders = { field: "", orderType: OrderType.Asc };
        this.recipesState.filters = { name: "Name", value: "" };
        this.searchTerm = "";
        $("#searchRecipes").val("").trigger("change.select2");
        this.saveRecipeListingSearchState();
        this.emitCategorySelectionChanged();
        this.requestRecipes(this.buildCurrentRecipeListRequest());
      })
      .on("select2:clear", () => {
        this.selectedCategoryId = null;
        this.recipesState.page = 1;
        this.recipesState.filters = { name: "", value: "" };
        this.searchTerm = "";
        this.saveRecipeListingSearchState();
        this.emitCategorySelectionChanged();
        this.requestRecipes(this.buildCurrentRecipeListRequest());
      });
  }

  applyRecipeSearchTerm(searchTerm: string) {
    const trimmedSearchTerm = String(searchTerm || "").trim();

    if (!trimmedSearchTerm) {
      this.resetRecipeFilters();
      return;
    }

    this.searchTerm = trimmedSearchTerm;
    this.recipesState.page = 1;
    this.recipesState.orders = { field: "Name", orderType: OrderType.Asc };
    this.recipesState.filters = { name: "Name", value: trimmedSearchTerm };

    const $searchRecipes = $("#searchRecipes");
    $searchRecipes
      .empty()
      .append(new Option(trimmedSearchTerm, trimmedSearchTerm, true, true))
      .trigger("change.select2");

    (<any>$searchRecipes).select2("close");
    this.saveRecipeListingSearchState();
    this.requestRecipes(this.buildCurrentRecipeListRequest());
  }

  /**
   * Initialization and event handling of recipe search (https://select2.org/)
   */
  initSearch() {
    // reset the select2 DOM node before reinit
    if ((<any>$("#searchRecipes")).hasClass("select2-hidden-accessible")) {
      (<any>$("#searchRecipes")).html("").select2();
    };
    const restoreSearchTerm = () => {
      this.$nextTick(() => {
        const select2 = $("#searchRecipes").data("select2");
        if (!select2 || !select2.dropdown || !select2.dropdown.$search) return;

        const $search = select2.dropdown.$search;
        const searchElement = $search.get(0);

        $search
          .val(this.searchTerm)
          .trigger("input")
          .off("input.fullreset")
          .on("input.fullreset", () => {
            this.searchTerm = String($search.val() || "");

            // If cleared, reset to full list
            if (!this.searchTerm) {
              this.resetRecipeFilters();
            }
          });

        if (searchElement) {
          const previousHandler = $search.data("recipeListingEnterHandler");

          if (previousHandler) {
            searchElement.removeEventListener("keydown", previousHandler, true);
          }

          const enterHandler = (event: KeyboardEvent) => {
            if (event.key !== "Enter") {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.applyRecipeSearchTerm(String($search.val() || ""));
          };

          $search.data("recipeListingEnterHandler", enterHandler);
          searchElement.addEventListener("keydown", enterHandler, true);
        }
      });
    };

    (<any>$("#searchRecipes"))
      .select2({
        allowClear: false,
        placeholder: this.isRecipe ? "Search Recipe" : "Search Combined Recipe",
        minimumInputLength: 3,
        width: "100%",
        ajax: {
          delay: 500,
          // method that handles user input and does the ajax call
          transport: (params: any, success: any) => {
            if(this.isRecipe){
              RecipesRepository.filterRecipesNames(
                params.data.term ? params.data.term : ""
              ).then((response) => {
                success(response);
              });
            }
            else{
              RecipesRepository.filterCombinedRecipeName(
                params.data.term ? params.data.term : ""
              ).then((response) => {
                success(response);
              });
            }
            this.searchTerm = params.data.term || "";
          },
          // on success
          processResults: (response: any) => {
            const data = this.isRecipe
              ? this.processRecipeSearchResults(this.searchTerm, response)
              : this.processCombinedRecipeSearchResults(this.searchTerm, response);

            return { results: data };
          },
        },
        escapeMarkup: function(element:string){
          return element;
        },
      })
      .on("select2:select", (eventData: any) => {
        let value: string = <string>eventData.target.value;

        this.recipesState.page = 1;
        this.recipesState.orders = { field: "", orderType: OrderType.Asc };

        if(value == "0"){
          this.recipesState.filters = { name: "", value: value };
          this.searchTerm = "";
        }
        else{
          this.recipesState.filters = { name: "Id", value: value };
          this.searchTerm = this.stripSearchResultMarkup(eventData?.params?.data?.text || "");
        }

        this.saveRecipeListingSearchState();

        // we do the filtering here no matter what value the filter got up there :)
        this.requestRecipes(
          this.buildCurrentRecipeListRequest()
        );

      })

      //restore term when dropdown reopens
      .on("select2:open", () => {
        restoreSearchTerm();
      });
  }

  resetRecipeFilters() {
    this.recipesState.page = 1;
    this.recipesState.filters = { name: "", value: "" };
    this.searchTerm = "";

    const $searchRecipes = $("#searchRecipes");
    $searchRecipes
      .empty()
      .val(null)
      .trigger("change.select2");

    this.saveRecipeListingSearchState();

    this.requestRecipes(
      this.buildCurrentRecipeListRequest()
    );
  }

  stripSearchResultMarkup(value: string) {
    return String(value || "").replace(/<[^>]*>/g, "");
  }

  processRecipeSearchResults(searchTerm: string, response: any){
    const filteredResults = filterRecipeSearchResultsByCategory(
      response.data.result,
      this.selectedCategoryId,
    );

    return collectRecipesFromCategoryResults(filteredResults, null)
      .map((recipe: Recipe) => ({
        id: recipe.id,
        text: getBoldString(recipe.name, searchTerm),
      }));
  }

  processCombinedRecipeSearchResults(searchTerm: string, response: any){
    let data: any[] = [];

    data.push({
      id: 0,
      text: this.translations.get("dish_all_dishes"),
    });
    response.data.result.map((r: any) => {
      data.push({ id: r.id, text: getBoldString(r.name, searchTerm) });
    });

    return data;
  }

}
